import { useEffect, useRef } from 'react';

import { useBasketHold } from '@common/hooks/useBasketHold';
import { useTranslations } from '@lang';

import { registerInlineHoldNotice, setDismissedHold, useDismissedHold } from './holdNoticePresence';
import { splitRemaining, WARN_FROM_MS } from './BasketHoldWarning';

type HoldNotice = {
  text: string;
  announcement: string;
  onDismiss: () => void;
  dismissLabel: string;
};

type HeldItem = { expireAt?: string | null };

export const useBasketHoldNotice = (
  items: HeldItem[] | undefined | null
): HoldNotice | undefined => {
  const { t } = useTranslations();
  const { state, msRemaining, expiresAt } = useBasketHold(items, WARN_FROM_MS);
  const dismissedHold = useDismissedHold();
  const showing = useRef(false);

  const remaining = msRemaining ?? 0;
  const hold = expiresAt?.getTime() ?? null;
  const isWarning = state === 'holding' && remaining <= WARN_FROM_MS && dismissedHold !== hold;

  useEffect(() => {
    if (!isWarning || showing.current) return undefined;

    showing.current = true;
    const unregister = registerInlineHoldNotice();

    return () => {
      showing.current = false;
      unregister();
    };
  }, [isWarning]);

  if (!isWarning) return undefined;

  const { minutes, seconds } = splitRemaining(remaining);
  const time =
    minutes > 0
      ? t('basket.holdRemainingMinutes', { minutes, seconds })
      : t('basket.holdRemainingSeconds', { seconds });

  return {
    text: t('basket.holdWarningTitle', { time }),
    announcement: t('basket.holdWarningAlert'),
    onDismiss: () => setDismissedHold(hold),
    dismissLabel: t('bookingRequestModal.dismissAlert'),
  };
};
